import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { Subscription, interval } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private currentHash: string | null = null;
  private intervalSub: Subscription | null = null;
  private notified = false;
  private inFlight = false;

  private static readonly TOAST_KEY = 'app-update';
  private static readonly NO_CACHE_HEADERS = new HttpHeaders({
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  });

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private zone: NgZone,
  ) {}

  initVersionCheck(intervalMinutes: number = 5): void {
    this.fetchIndex().then(hash => {
      this.currentHash = hash;
    });

    this.intervalSub?.unsubscribe();
    this.intervalSub = interval(intervalMinutes * 60 * 1000).subscribe(() => {
      this.checkForUpdates();
    });

    // iOS PWA замораживает таймеры в фоне — проверяем при возврате на видимость.
    // Это ключевой триггер: пользователь развернул приложение → сразу свежая проверка.
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // bfcache на iOS: страница восстанавливается из памяти без перезапуска JS.
    window.addEventListener('pageshow', this.onPageShow);
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.checkForUpdates();
    }
  };

  private onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) this.checkForUpdates();
  };

  private checkForUpdates(): void {
    if (this.inFlight || this.notified) return;
    this.inFlight = true;

    this.fetchIndex()
      .then(newHash => {
        if (this.currentHash && newHash && this.currentHash !== newHash) {
          this.notifyUserOfUpdate();
        }
      })
      .finally(() => {
        this.inFlight = false;
      });
  }

  private fetchIndex(): Promise<string | null> {
    // cache-buster + no-store-заголовки — единственный способ обойти кеш iOS WebKit
    // (особенно когда сайт открыт как standalone PWA с домашнего экрана).
    const url = `/index.html?_=${Date.now()}`;
    return this.http
      .get(url, { responseType: 'text', headers: VersionCheckService.NO_CACHE_HEADERS })
      .toPromise()
      .then(html => (html ? this.hashCode(html) : null))
      .catch(() => null);
  }

  private notifyUserOfUpdate(): void {
    this.notified = true;
    // Через NgZone, потому что toast может прилететь из visibilitychange/pageshow,
    // которые иногда выпадают из zone — иначе UI не обновится.
    this.zone.run(() => {
      this.messageService.add({
        key: VersionCheckService.TOAST_KEY,
        severity: 'info',
        summary: 'Доступна новая версия',
        detail: 'Нажмите кнопку, чтобы обновить приложение',
        sticky: true,
        closable: true,
      });
    });
  }

  /** Вызывается из app.component на (onClose)/(onClick) тоста — см. шаблон. */
  reload(): void {
    this.notified = false;
    // ?_v= — чтобы iOS гарантированно не отдал index.html из кеша при reload.
    const sep = location.search.includes('?') ? '&' : '?';
    const fresh = `${location.pathname}${location.search}${sep}_v=${Date.now()}${location.hash}`;
    location.replace(fresh);
  }

  private hashCode(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  }
}
